/**
 * localStorage-backed session store.
 *
 * The playground persists every session + its message history client-side
 * so the demo survives a page reload. Backend session persistence is
 * Phase 2 (Sesi BE-GOVERNANCE-SSE deferred ``playground_sessions`` table).
 *
 * Quota:
 *
 * * Each message is capped at ``MAX_MESSAGE_BYTES``; the composer enforces.
 * * Total payload across sessions is capped at ``MAX_TOTAL_BYTES``; on
 *   overflow we FIFO-evict the oldest session and surface a toast.
 */

import type {
  PlaygroundMessage,
  PlaygroundSession,
  SessionIndex,
} from "./types";

const NS = "monago.playground";

export const STORAGE_KEYS = {
  INDEX: `${NS}.sessions`,
  ACTIVE: `${NS}.activeSessionId`,
  GOV_OPEN: `${NS}.ui.govPanelOpen`,
  session: (id: string) => `${NS}.session:${id}`,
} as const;

export const MAX_MESSAGE_BYTES = 16 * 1024;
export const MAX_TOTAL_BYTES = 5 * 1024 * 1024;

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* swallow */
  }
}

/** Estimated UTF-8 byte length of a string (Blob avoids encoder allocation). */
export function utf8ByteLength(value: string): number {
  return new Blob([value]).size;
}

/** Read the recent-first session index. */
export function loadSessionIndex(): SessionIndex {
  const raw = safeGet(STORAGE_KEYS.INDEX);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed.filter((s) => typeof s === "string") as string[]) : [];
  } catch {
    return [];
  }
}

export function saveSessionIndex(index: SessionIndex): void {
  safeSet(STORAGE_KEYS.INDEX, JSON.stringify(index));
}

export function loadActiveSessionId(): string | null {
  return safeGet(STORAGE_KEYS.ACTIVE);
}

export function saveActiveSessionId(id: string | null): void {
  if (id) safeSet(STORAGE_KEYS.ACTIVE, id);
  else safeRemove(STORAGE_KEYS.ACTIVE);
}

export function loadSession(id: string): PlaygroundSession | null {
  const raw = safeGet(STORAGE_KEYS.session(id));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PlaygroundSession;
    if (!parsed || typeof parsed !== "object" || parsed.id !== id) return null;
    parsed.messages = parsed.messages ?? [];
    parsed.skillRef = parsed.skillRef ?? null;
    parsed.stats = parsed.stats ?? { tokens: 0, cost: 0, violations: 0 };
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist a session, FIFO-evicting the oldest entries when the total
 * payload would exceed ``MAX_TOTAL_BYTES``. Returns the evicted ids so
 * the caller can drop them from any in-memory cache.
 */
export function saveSession(
  session: PlaygroundSession,
  index: SessionIndex
): { evicted: string[] } {
  const payload = JSON.stringify(session);
  if (payload.length > MAX_MESSAGE_BYTES * 64) {
    // Soft guardrail — even a long conversation shouldn't blow past this.
    // The composer enforces the per-message cap separately.
  }

  // Best-effort total budget.
  const totals = totalBytesAcross(index, session.id, payload.length);
  const evicted: string[] = [];
  let remainingIndex = [...index];
  let projected = totals;
  while (projected > MAX_TOTAL_BYTES && remainingIndex.length > 1) {
    const victim = remainingIndex.pop();
    if (!victim || victim === session.id) break;
    const victimRaw = safeGet(STORAGE_KEYS.session(victim));
    safeRemove(STORAGE_KEYS.session(victim));
    if (victimRaw) projected -= victimRaw.length;
    evicted.push(victim);
  }

  safeSet(STORAGE_KEYS.session(session.id), payload);
  saveSessionIndex(remainingIndex);
  return { evicted };
}

function totalBytesAcross(
  index: SessionIndex,
  skipId: string,
  inflightBytes: number
): number {
  let total = inflightBytes;
  for (const id of index) {
    if (id === skipId) continue;
    const raw = safeGet(STORAGE_KEYS.session(id));
    if (raw) total += raw.length;
  }
  return total;
}

export function deleteSession(id: string): void {
  safeRemove(STORAGE_KEYS.session(id));
}

export function loadGovPanelOpen(): boolean {
  const raw = safeGet(STORAGE_KEYS.GOV_OPEN);
  if (raw === null) return true;
  return raw !== "false";
}

export function saveGovPanelOpen(open: boolean): void {
  safeSet(STORAGE_KEYS.GOV_OPEN, String(open));
}

/**
 * Build an empty session with sensible defaults. The first message
 * derives the title; until then "Untitled session" is the placeholder.
 */
export function createBlankSession(input: {
  /**
   * Pre-selected model, or ``null`` when the operator must pick one
   * from the catalog before sending. Default ``null`` matches the
   * "no hardcoded default" rule — see PlaygroundSession.model.
   */
  model?: string | null;
  systemPrompt?: string;
  systemPromptLocked?: boolean;
} = {}): PlaygroundSession {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title: "Untitled session",
    createdAt: now,
    updatedAt: now,
    model: input.model ?? null,
    systemPrompt: input.systemPrompt ?? "",
    systemPromptLocked: input.systemPromptLocked ?? false,
    skillRef: null,
    messages: [],
    stats: { tokens: 0, cost: 0, violations: 0 },
  };
}

export function newMessageId(): string {
  return crypto.randomUUID();
}

/** Derive a title from the first user message; capped at 60 chars. */
export function deriveTitle(message: PlaygroundMessage): string {
  const flat = message.content.replace(/\s+/g, " ").trim();
  if (!flat) return "Untitled session";
  return flat.length > 60 ? `${flat.slice(0, 57)}…` : flat;
}
